import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'

export interface UploadRow {
  id: string
  name?: string
  isNew: boolean
  raw: Record<string, unknown>
  /** Flat representation of the existing record, for diffing (updated rows only) */
  before?: Record<string, unknown>
}

interface UploadConfirmModalProps {
  rows: UploadRow[]
  open: boolean
  onConfirm: (rows: Record<string, unknown>[]) => Promise<void>
  onCancel: () => void
}

const TIME_FIELDS = new Set(['updateTime', 'createTime', 'lastUpdated'])

function getChangedFields(r: UploadRow): [string, unknown, unknown][] {
  if (!r.before) return []
  return Object.entries(r.before)
    .filter(([k, v]) => {
      if (TIME_FIELDS.has(k)) return false
      return String(v ?? '') !== String((r.raw as Record<string, unknown>)[k] ?? '')
    })
    .map(([k, v]) => [k, v, (r.raw as Record<string, unknown>)[k]])
}

export function UploadConfirmModal({ rows, open, onConfirm, onCancel }: UploadConfirmModalProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  const newRows = rows.filter((r) => r.isNew)
  const updatedRows = rows.filter((r) => !r.isNew && getChangedFields(r).length > 0)

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await onConfirm([...newRows, ...updatedRows].map((r) => r.raw))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('importExport.confirmTitle')}</DialogTitle>
          {(newRows.length > 0 || updatedRows.length > 0) && (
            <DialogDescription>{t('importExport.confirmDescription')}</DialogDescription>
          )}
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto space-y-6 py-1">
          {/* No changes */}
          {newRows.length === 0 && updatedRows.length === 0 && (
            <p className="text-sm text-(--ad-text-muted) py-4 text-center">{t('importExport.noChanges')}</p>
          )}

          {/* New rows */}
          {newRows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="success">{t('common.new')}</Badge>
                <span className="text-sm font-medium">
                  {t('importExport.newItems', { count: newRows.length })}
                </span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.name')}</TableHead>
                    <TableHead className="text-(--ad-text-muted) font-normal">ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {newRows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.name ?? '—'}</TableCell>
                      <TableCell className="font-mono text-xs text-(--ad-text-muted)">{r.id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Updated rows */}
          {updatedRows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="blue">{t('importExport.updated')}</Badge>
                <span className="text-sm font-medium">
                  {t('importExport.updatedItems', { count: updatedRows.length })}
                </span>
              </div>
              <div className="space-y-4">
                {updatedRows.map((r) => {
                  const changes = getChangedFields(r)
                  return (
                    <div key={r.id} className="rounded-md border border-(--ad-border) overflow-hidden">
                      <div className="px-3 py-2 bg-muted/40 flex items-center gap-2 border-b border-(--ad-border)">
                        <span className="text-sm font-medium">{r.name ?? r.id}</span>
                        {r.name && <span className="text-xs font-mono text-(--ad-text-muted)">{r.id}</span>}
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-36">{t('common.field')}</TableHead>
                            <TableHead>{t('importExport.oldValue')}</TableHead>
                            <TableHead>{t('importExport.newValue')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {changes.map(([field, oldVal, newVal]) => (
                            <TableRow key={field}>
                              <TableCell className="text-xs text-(--ad-text-muted) font-mono">{field}</TableCell>
                              <TableCell className="text-xs font-mono line-through text-(--ad-danger)">{String(oldVal ?? '')}</TableCell>
                              <TableCell className="text-xs font-mono text-green-600 dark:text-green-400">{String(newVal ?? '')}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            {newRows.length === 0 && updatedRows.length === 0 ? t('common.close') : t('common.cancel')}
          </Button>
          {(newRows.length > 0 || updatedRows.length > 0) && (
            <Button onClick={handleConfirm} disabled={loading}>
              {loading ? t('common.saving') : t('importExport.confirmUpload')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
